import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Timeline, type TimelineEntry } from '@/components/shared/timeline';
import { UnityLogo } from '@/components/shared/unity-logo';
import { Avatar } from '@/components/ui/avatar';
import { SupportTicketAttachments } from '@/pages/support/support-ticket-attachments';
import type {
  SupportTicketEvent,
  SupportTicketMessage,
  SupportTicketPriority,
  SupportTicketStatus,
} from '@/pages/support/types';
import { cn } from '@/utils/cn';

type TimelineItem =
  | { kind: 'message'; at: string; id: string; message: SupportTicketMessage }
  | { kind: 'event'; at: string; id: string; event: SupportTicketEvent };

const WAITING_STATUSES = new Set<SupportTicketStatus>(['awaiting_church', 'awaiting_platform']);

/** Reply-driven "waiting on" flips stay in the database. The church thread does not show them. */
const isWaitingStatusEvent = (event: SupportTicketEvent): boolean => {
  if (event.kind !== 'status_changed') return false;
  const after = readStatus(event.afterValue);
  return after !== null && WAITING_STATUSES.has(after);
};

const readStatus = (value: Record<string, unknown> | null): SupportTicketStatus | null => {
  const raw = value?.status;
  return typeof raw === 'string' ? (raw as SupportTicketStatus) : null;
};

const readPriority = (value: Record<string, unknown> | null): SupportTicketPriority | null => {
  const raw = value?.priority;
  return typeof raw === 'string' ? (raw as SupportTicketPriority) : null;
};

const eventToTimelineEntry = (
  event: SupportTicketEvent,
  t: (key: string, options?: Record<string, unknown>) => string,
): TimelineEntry => {
  const actor =
    event.actorName ??
    (event.actorSide === 'system' ? t('timeline.actor.system') : t('timeline.actor.unknown'));

  let title = t(`timeline.event.${event.kind}`);
  let description: string | undefined;

  switch (event.kind) {
    case 'status_changed': {
      const before = readStatus(event.beforeValue);
      const after = readStatus(event.afterValue);
      if (before && after) {
        description = t('timeline.statusChanged', {
          before: t(`status.${before}`),
          after: t(`status.${after}`),
        });
      }
      break;
    }
    case 'priority_changed': {
      const before = readPriority(event.beforeValue);
      const after = readPriority(event.afterValue);
      if (before && after) {
        description = t('timeline.priorityChanged', {
          before: t(`priority.${before}`),
          after: t(`priority.${after}`),
        });
      }
      break;
    }
    case 'assigned':
      description = event.afterValue?.assignedPlatformName
        ? t('timeline.assignedTo', {
            name: String(event.afterValue.assignedPlatformName),
          })
        : undefined;
      break;
    case 'unassigned':
      description = t('timeline.unassigned');
      break;
    case 'escalated':
      description = t('timeline.escalated');
      break;
    default:
      break;
  }

  return {
    id: event.id,
    at: event.createdAt,
    title,
    description,
    actor,
    tone:
      event.kind === 'escalated' || event.kind === 'closed'
        ? 'warning'
        : event.kind === 'resolved'
          ? 'success'
          : 'neutral',
    icon:
      event.actorSide === 'church' ? (
        <Avatar name={actor} src={event.actorAvatarUrl} size="sm" />
      ) : (
        <UnityLogo variant="symbol-color" className="size-8" label={t('timeline.actor.support')} />
      ),
  };
};

export const SupportTicketTimeline = ({
  messages,
  events,
}: {
  messages: SupportTicketMessage[];
  events: SupportTicketEvent[];
}) => {
  const { t } = useTranslation('support');

  const items = useMemo(() => {
    const merged: TimelineItem[] = [
      ...messages.map((message) => ({
        kind: 'message' as const,
        at: message.createdAt,
        id: message.id,
        message,
      })),
      ...events
        .filter((event) => event.kind !== 'message' && !isWaitingStatusEvent(event))
        .map((event) => ({
          kind: 'event' as const,
          at: event.createdAt,
          id: event.id,
          event,
        })),
    ];
    merged.sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
    return merged;
  }, [events, messages]);

  const unityMark = () => (
    <UnityLogo variant="symbol-color" className="size-8" label={t('timeline.actor.support')} />
  );

  const entries: TimelineEntry[] = items.map((item) => {
    if (item.kind === 'event') return eventToTimelineEntry(item.event, t);

    const message = item.message;
    const isChurch = message.authorSide === 'church';
    return {
      id: message.id,
      at: message.createdAt,
      title:
        message.authorName ??
        (isChurch ? t('timeline.actor.you') : t('timeline.actor.support')),
      icon: isChurch ? (
        <Avatar name={message.authorName ?? t('timeline.actor.you')} src={message.authorAvatarUrl} size="sm" />
      ) : (
        unityMark()
      ),
      tone: isChurch ? 'info' : 'neutral',
      content: (
        <div
          className={cn(
            'w-fit max-w-full rounded-lg border px-3 py-2',
            isChurch ? 'border-brand/20 bg-brand/5' : 'border-border bg-surface',
          )}
        >
          <p className="whitespace-pre-wrap break-words text-body-sm text-fg">{message.body}</p>
          <SupportTicketAttachments attachments={message.attachments} />
        </div>
      ),
    };
  });

  if (entries.length === 0) {
    return <p className="text-body-sm text-fg-secondary">{t('timeline.empty')}</p>;
  }

  return <Timeline entries={entries} />;
};
